import type { PrinterProfile } from './printerProfile';
import type { QrArea } from '../../platform/qrPolarity';

export interface CalibrationMeasurements {
  /** The printed X/Y grid mark lying exactly at the material's left/top edge. */
  xAtLeftMm: number;
  yAtTopMm: number;
  /** Two marks measured with a ruler for each axis. */
  xStartMm: number;
  xEndMm: number;
  xMeasuredMm: number;
  yStartMm: number;
  yEndMm: number;
  yMeasuredMm: number;
}

export function headWidthMm(inches: 2 | 3 | 4): number {
  return ({ 2: 58, 3: 80, 4: 104 })[inches];
}

export function initialCalibration(profile: PrinterProfile): PrinterProfile {
  const width = profile.labelWidthMm;
  const centred = profile.labelPosition === 'center' && profile.headWidthMm > width;
  // HZZT 9260 reports a 4-inch head but its TSPL origin sits six millimetres
  // farther right than the geometrical centre. -29 mm is the measured
  // workshop baseline for a 58 mm roll; do not discard it when restarting a
  // Y-scale measurement.
  const xOffsetMm = profile.model === 'hzzt9260' && centred
    ? -29
    : centred ? -(profile.headWidthMm - width) / 2 : 0;
  return {
    ...profile,
    xOffsetMm,
    yOffsetMm: 0,
    scaleX: 1,
    scaleY: 1,
  };
}

function factor(start: number, end: number, measured: number): number {
  const expected = Math.abs(end - start);
  if (!Number.isFinite(expected) || !Number.isFinite(measured) || expected <= 0 || measured <= 0) throw new Error('invalid_measurement');
  return expected / measured;
}

/**
 * A calibration raster is first enlarged/shrunk by the existing scale.  The
 * next correction therefore multiplies it rather than replacing it, allowing
 * repeat prints to converge.  Offsets are in nominal bitmap millimetres.
 */
export function applyCalibration(profile: PrinterProfile, measured: CalibrationMeasurements): PrinterProfile {
  const scaleX = profile.scaleX * factor(measured.xStartMm, measured.xEndMm, measured.xMeasuredMm);
  const scaleY = profile.scaleY * factor(measured.yStartMm, measured.yEndMm, measured.yMeasuredMm);
  if (scaleX < 0.8 || scaleX > 1.2 || scaleY < 0.8 || scaleY > 1.2) throw new Error('scale_out_of_range');
  return {
    ...profile,
    scaleX,
    scaleY,
    xOffsetMm: profile.xOffsetMm - measured.xAtLeftMm * scaleX,
    yOffsetMm: profile.yOffsetMm - measured.yAtTopMm * scaleY,
  };
}

export interface PrinterSettingsBitmap {
  blob: Blob;
  qrAreas: QrArea[];
}

/** Render the final label: legible settings plus a QR that reopens this profile. */
export async function printerSettingsBitmap(
  profile: PrinterProfile,
  qr: Blob,
  details: { profileName?: string; shortCode?: string; printedAt?: Date } = {},
): Promise<PrinterSettingsBitmap> {
  // A D110 label can be only 12 mm high.  Do not grow it to 20 mm merely to
  // make room for the large-printer layout: the BLE driver must receive a
  // raster no wider than its thermal head.
  const width = Math.max(20, profile.labelWidthMm || 58);
  const height = Math.max(8, profile.labelHeightMm || 40);
  const dots = 8;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(width * dots / 8) * 8;
  canvas.height = Math.round(height * dots);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('raster_failed');
  context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#000';
  const qrImage = await createImageBitmap(qr);
  const padding = dots * 2;
  const qrSize = Math.max(1, Math.min(Math.round(dots * 28), canvas.height - padding * 2, Math.round(canvas.width * .42)));
  const qrX = canvas.width - padding - qrSize;
  const title = Math.max(12, Math.round(dots * 2.5));
  const text = Math.max(9, Math.round(dots * 1.65));
  const printedAt = (details.printedAt ?? new Date()).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  const large = width >= 75 || height >= 58;
  const compact = height < 20;
  const lines = compact ? [
    details.profileName ? `Профиль: ${details.profileName}` : 'StorageAPP · профиль',
    `Дата: ${printedAt}`,
  ] : [
    details.profileName ? `Профиль: ${details.profileName}` : 'StorageAPP · Профиль',
    details.shortCode ? `QR-ссылка: /p/${details.shortCode}` : 'QR-ссылка на профиль',
    `Напечатано: ${printedAt}`,
    ...(large ? [
      `Рулон: ${profile.labelPosition === 'center' ? 'центр' : 'край'} · головка ${profile.headWidthMm || 58} мм`,
      `Этикетка: ${width} × ${height} мм · GAP ${profile.gapMm} мм`,
      `X ${profile.xOffsetMm.toFixed(2)} · Y ${profile.yOffsetMm.toFixed(2)} мм`,
      `Масштаб X ${(profile.scaleX * 100).toFixed(2)}% · Y ${(profile.scaleY * 100).toFixed(2)}%`,
      `Подача ${profile.feedMm.toFixed(2)} · зазор ${profile.gapOffsetMm.toFixed(2)} мм`,
      `Инверсия: ${profile.inverted ? 'да' : 'нет'}`,
    ] : []),
  ];
  let y = Math.round(dots * 3.5);
  context.font = `bold ${title}px sans-serif`; context.fillText(lines[0] ?? '', padding, y, Math.max(1, qrX - padding * 2));
  context.font = `${text}px sans-serif`;
  for (const line of lines.slice(1)) { y += Math.round(dots * 2.7); if (y > canvas.height - padding) break; context.fillText(line, padding, y, Math.max(1, qrX - padding * 2)); }
  context.fillStyle = '#fff'; context.fillRect(qrX, padding, qrSize, qrSize);
  context.imageSmoothingEnabled = false;
  context.drawImage(qrImage, qrX, padding, qrSize, qrSize);
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error('raster_failed')), 'image/png'));
  return { blob, qrAreas: [{ x: qrX, y: padding, w: qrSize, h: qrSize, unit: 'px' }] };
}
